import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Car,
  Search,
  Plus,
  Edit,
  Archive,
  ArchiveRestore,
  Star,
  StarOff,
  DollarSign,
  Loader2,
  ArrowLeft,
  Eye,
  EyeOff,
  Filter,
  AlertTriangle,
  X,
  Tag,
  ShoppingCart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Get auth headers
const getAuthHeaders = () => {
  const token = sessionStorage.getItem('dashboard_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Format date helper
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: '2-digit'
  });
};

// Format price helper
const formatPrice = (price) => {
  return `€${price?.toLocaleString() || 0}`;
};

// Confirmation Modal Component
const ConfirmModal = ({ isOpen, onClose, onConfirm, title, message, confirmText, confirmColor, isLoading }) => {
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-[#0A0F14] border border-white/20 p-6 max-w-md w-full"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 flex items-center justify-center bg-orange-500/20 rounded">
              <AlertTriangle size={20} className="text-orange-400" />
            </div>
            <h3 className="font-serif text-xl font-bold text-[#F8FAFC]">{title}</h3>
          </div>
          
          <p className="text-[#94A3B8] font-sans text-sm mb-6 leading-relaxed whitespace-pre-line">
            {message}
          </p>
          
          <div className="flex justify-end gap-3">
            <Button
              onClick={onClose}
              disabled={isLoading}
              variant="outline"
              className="h-10 px-4 bg-transparent border-white/20 text-[#94A3B8] hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={onConfirm}
              disabled={isLoading}
              className={`h-10 px-4 ${confirmColor} text-white`}
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                confirmText
              )}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Car Row Component
const CarRow = ({ car, onToggleFeatured, onToggleFinance, onMarkSold, onMarkAvailable, onArchive, onUnarchive, onEdit, isUpdating }) => {
  const isSold = car.status === 'sold';
  const isArchived = car.archived;
  
  return (
    <tr className={`border-b border-white/10 hover:bg-white/5 transition-colors ${isArchived ? 'opacity-60' : ''}`}>
      {/* Cover Image */}
      <td className="py-3 px-2">
        <div className="w-16 h-12 overflow-hidden bg-[#16202A]">
          <img
            src={car.images?.[0] || 'https://via.placeholder.com/100x75?text=No+Image'}
            alt={`${car.make} ${car.model}`}
            className="w-full h-full object-cover"
          />
        </div>
      </td>
      
      {/* Vehicle */}
      <td className="py-3 px-2">
        <div>
          <p className="text-[#F8FAFC] font-sans text-sm font-medium">
            {car.year} {car.make} {car.model}
          </p>
          {car.trim && (
            <p className="text-[#64748B] font-sans text-xs">{car.trim}</p>
          )}
        </div>
      </td>
      
      {/* Price */}
      <td className="py-3 px-2">
        <p className="text-[#48CCCD] font-sans text-sm font-bold">{formatPrice(car.price)}</p>
      </td>
      
      {/* Status with Mark Sold/Available Button */}
      <td className="py-3 px-2">
        <div className="flex items-center gap-2">
          <span 
            className={`px-2 py-1 text-[10px] uppercase tracking-wider font-sans font-bold ${
              isArchived 
                ? 'bg-gray-500/20 text-gray-400' 
                : isSold 
                  ? 'bg-red-500/20 text-red-400' 
                  : 'bg-green-500/20 text-green-400'
            }`}
            data-testid={`status-badge-${car.id}`}
          >
            {isArchived ? 'Archived' : isSold ? 'SOLD' : 'Available'}
          </span>
          
          {/* Mark Sold / Mark Available Button */}
          {!isArchived && (
            isSold ? (
              <button
                onClick={() => onMarkAvailable(car)}
                disabled={isUpdating}
                className="px-2 py-1 text-[10px] uppercase tracking-wider font-sans font-bold bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Mark as available"
                data-testid={`mark-available-btn-${car.id}`}
              >
                <span className="hidden sm:inline">Mark Available</span>
                <span className="sm:hidden">Avail</span>
              </button>
            ) : (
              <button
                onClick={() => onMarkSold(car)}
                disabled={isUpdating}
                className="px-2 py-1 text-[10px] uppercase tracking-wider font-sans font-bold bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Mark as sold"
                data-testid={`mark-sold-btn-${car.id}`}
              >
                <span className="hidden sm:inline">Mark Sold</span>
                <span className="sm:hidden">Sold</span>
              </button>
            )
          )}
        </div>
      </td>
      
      {/* Featured */}
      <td className="py-3 px-2 text-center">
        <button
          onClick={() => onToggleFeatured(car)}
          disabled={isUpdating || isArchived}
          className="p-1.5 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed rounded"
          title={car.featured ? 'Remove from featured' : 'Add to featured'}
          data-testid={`featured-btn-${car.id}`}
        >
          {car.featured ? (
            <Star size={16} className="text-yellow-400 fill-yellow-400" />
          ) : (
            <StarOff size={16} className="text-[#64748B]" />
          )}
        </button>
      </td>
      
      {/* Finance */}
      <td className="py-3 px-2 text-center">
        <button
          onClick={() => onToggleFinance(car)}
          disabled={isUpdating || isArchived}
          className="p-1.5 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed rounded"
          title={car.finance_enabled ? 'Disable finance' : 'Enable finance'}
          data-testid={`finance-btn-${car.id}`}
        >
          <DollarSign size={16} className={car.finance_enabled ? 'text-green-400' : 'text-[#64748B]'} />
        </button>
      </td>
      
      {/* Updated */}
      <td className="py-3 px-2">
        <p className="text-[#94A3B8] font-sans text-xs">{formatDate(car.updated_at)}</p>
        {isSold && car.sold_at && (
          <p className="text-red-400/70 font-sans text-[10px]">Sold: {formatDate(car.sold_at)}</p>
        )}
      </td>
      
      {/* Actions */}
      <td className="py-3 px-2">
        <div className="flex items-center gap-1">
          {/* Edit */}
          <button
            onClick={() => onEdit(car)}
            disabled={isUpdating}
            className="p-1.5 hover:bg-white/10 text-[#48CCCD] disabled:opacity-50 rounded"
            title="Edit car"
            data-testid={`edit-btn-${car.id}`}
          >
            <Edit size={14} />
          </button>
          
          {/* Archive / Unarchive */}
          {isArchived ? (
            <button
              onClick={() => onUnarchive(car)}
              disabled={isUpdating}
              className="p-1.5 hover:bg-white/10 text-blue-400 disabled:opacity-50 rounded"
              title="Restore from archive"
              data-testid={`unarchive-btn-${car.id}`}
            >
              <ArchiveRestore size={14} />
            </button>
          ) : (
            <button
              onClick={() => onArchive(car)}
              disabled={isUpdating}
              className="p-1.5 hover:bg-white/10 text-orange-400 disabled:opacity-50 rounded"
              title="Archive car"
              data-testid={`archive-btn-${car.id}`}
            >
              <Archive size={14} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

const DashboardCarsPage = () => {
  const navigate = useNavigate();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [updatingCars, setUpdatingCars] = useState(new Set());
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [featuredFilter, setFeaturedFilter] = useState('all');
  const [financeFilter, setFinanceFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  
  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    car: null,
    action: null, // 'sold' | 'available'
    isLoading: false
  });
  
  const PAGE_SIZE = 50;

  // Check auth on mount
  useEffect(() => {
    const token = sessionStorage.getItem('dashboard_token');
    if (!token) {
      navigate('/dashboard/login');
    }
  }, [navigate]);

  // Fetch cars
  const fetchCars = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', PAGE_SIZE.toString());
      if (searchQuery) params.append('q', searchQuery);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (featuredFilter !== 'all') params.append('featured', featuredFilter === 'featured' ? 'true' : 'false');
      if (financeFilter !== 'all') params.append('finance_enabled', financeFilter === 'enabled' ? 'true' : 'false');
      if (showArchived) params.append('show_archived', 'true');
      
      const response = await fetch(`${API}/dashboard/cars?${params}`, {
        headers: getAuthHeaders()
      });
      
      if (response.status === 401) {
        navigate('/dashboard/login');
        return;
      }
      
      if (!response.ok) throw new Error('Failed to fetch cars');
      
      const data = await response.json();
      setCars(data.cars);
      setTotal(data.total);
    } catch (error) {
      console.error('Failed to fetch cars:', error);
      toast.error('Failed to load cars');
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, statusFilter, featuredFilter, financeFilter, showArchived, navigate]);

  useEffect(() => {
    fetchCars();
  }, [fetchCars]);

  // Update car helper
  const updateCar = async (carId, updates, successMessage) => {
    setUpdatingCars(prev => new Set([...prev, carId]));
    try {
      const response = await fetch(`${API}/dashboard/cars/${carId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(updates)
      });
      
      if (response.status === 401) {
        navigate('/dashboard/login');
        return false;
      }
      
      if (!response.ok) throw new Error('Failed to update car');
      
      const updatedCar = await response.json();
      setCars(prev => prev.map(c => c.id === carId ? updatedCar : c));
      toast.success(successMessage);
      return true;
    } catch (error) {
      console.error('Failed to update car:', error);
      toast.error('Failed to update car');
      return false;
    } finally {
      setUpdatingCars(prev => {
        const next = new Set(prev);
        next.delete(carId);
        return next;
      });
    }
  };

  // Action handlers
  const handleToggleFeatured = (car) => {
    updateCar(car.id, { featured: !car.featured }, 
      car.featured ? 'Removed from featured' : 'Added to featured');
  };

  const handleToggleFinance = (car) => {
    updateCar(car.id, { finance_enabled: !car.finance_enabled },
      car.finance_enabled ? 'Finance disabled' : 'Finance enabled');
  };

  // Mark Sold - opens confirmation modal
  const handleMarkSoldClick = (car) => {
    setConfirmModal({
      isOpen: true,
      car,
      action: 'sold',
      isLoading: false
    });
  };

  // Mark Available - opens confirmation modal
  const handleMarkAvailableClick = (car) => {
    setConfirmModal({
      isOpen: true,
      car,
      action: 'available',
      isLoading: false
    });
  };

  // Confirm mark sold/available
  const handleConfirmStatusChange = async () => {
    const { car, action } = confirmModal;
    if (!car) return;
    
    setConfirmModal(prev => ({ ...prev, isLoading: true }));
    
    const newStatus = action === 'sold' ? 'sold' : 'available';
    const message = action === 'sold' 
      ? `${car.year} ${car.make} ${car.model} marked as SOLD`
      : `${car.year} ${car.make} ${car.model} is now available`;
    
    const success = await updateCar(car.id, { status: newStatus }, message);
    
    if (success) {
      setConfirmModal({ isOpen: false, car: null, action: null, isLoading: false });
    } else {
      setConfirmModal(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleCloseModal = () => {
    if (!confirmModal.isLoading) {
      setConfirmModal({ isOpen: false, car: null, action: null, isLoading: false });
    }
  };

  const handleArchive = async (car) => {
    setUpdatingCars(prev => new Set([...prev, car.id]));
    try {
      const response = await fetch(`${API}/dashboard/cars/${car.id}/archive`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.status === 401) {
        navigate('/dashboard/login');
        return;
      }
      
      if (!response.ok) throw new Error('Failed to archive car');
      
      // Remove from list if not showing archived
      if (!showArchived) {
        setCars(prev => prev.filter(c => c.id !== car.id));
        setTotal(prev => prev - 1);
      } else {
        // Update in list
        setCars(prev => prev.map(c => c.id === car.id ? { ...c, archived: true } : c));
      }
      toast.success('Car archived');
    } catch (error) {
      console.error('Failed to archive car:', error);
      toast.error('Failed to archive car');
    } finally {
      setUpdatingCars(prev => {
        const next = new Set(prev);
        next.delete(car.id);
        return next;
      });
    }
  };

  const handleUnarchive = async (car) => {
    setUpdatingCars(prev => new Set([...prev, car.id]));
    try {
      const response = await fetch(`${API}/dashboard/cars/${car.id}/unarchive`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.status === 401) {
        navigate('/dashboard/login');
        return;
      }
      
      if (!response.ok) throw new Error('Failed to unarchive car');
      
      setCars(prev => prev.map(c => c.id === car.id ? { ...c, archived: false, archived_at: null } : c));
      toast.success('Car restored');
    } catch (error) {
      console.error('Failed to unarchive car:', error);
      toast.error('Failed to restore car');
    } finally {
      setUpdatingCars(prev => {
        const next = new Set(prev);
        next.delete(car.id);
        return next;
      });
    }
  };

  const handleEdit = (car) => {
    navigate(`/dashboard/cars/${car.id}/edit`);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchCars();
  };

  // Get modal content based on action
  const getModalContent = () => {
    const { car, action } = confirmModal;
    if (!car) return {};
    
    if (action === 'sold') {
      return {
        title: 'Mark as SOLD?',
        message: `Mark "${car.year} ${car.make} ${car.model}" as SOLD? It will stay visible on the website as SOLD for 14 days (enquiries disabled), then it will be archived automatically.\n\nYou can undo this by clicking "Mark Available".`,
        confirmText: 'Mark Sold',
        confirmColor: 'bg-red-600 hover:bg-red-700'
      };
    } else {
      return {
        title: 'Mark as Available?',
        message: `Mark "${car.year} ${car.make} ${car.model}" as available? It will reappear on the public site.`,
        confirmText: 'Mark Available',
        confirmColor: 'bg-green-600 hover:bg-green-700'
      };
    }
  };

  const modalContent = getModalContent();

  return (
    <div className="min-h-screen bg-[#020405] pt-20" data-testid="dashboard-cars-page">
      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmStatusChange}
        title={modalContent.title}
        message={modalContent.message}
        confirmText={modalContent.confirmText}
        confirmColor={modalContent.confirmColor}
        isLoading={confirmModal.isLoading}
      />
      
      {/* Header */}
      <section className="py-8 md:py-12 bg-gradient-to-br from-[#16202A] via-[#0A0F14] to-[#020405]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                size="sm"
                className="h-10 px-3 bg-transparent border border-white/20 text-[#94A3B8] hover:text-white hover:border-white/40"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft size={18} />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center bg-[#48CCCD]/20">
                  <Car size={24} className="text-[#48CCCD]" />
                </div>
                <div>
                  <h1 className="font-serif text-2xl md:text-3xl font-bold text-[#F8FAFC] tracking-tight">
                    Manage Cars
                  </h1>
                  <p className="text-xs text-[#94A3B8] font-sans">
                    {total} car{total !== 1 ? 's' : ''} in inventory
                  </p>
                </div>
              </div>
            </div>
            
            <Button
              onClick={() => navigate('/dashboard/cars/new')}
              className="h-10 px-4 bg-[#48CCCD] text-[#020405] hover:bg-[#3db8b8] font-sans text-xs uppercase tracking-wider"
              data-testid="add-car-btn"
            >
              <Plus size={16} className="mr-2" />
              Add Car
            </Button>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-4 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <form onSubmit={handleSearch} className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="flex-1 min-w-[180px] relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
              <Input
                type="text"
                placeholder="Search make, model, year..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-10"
                data-testid="search-input"
              />
            </div>
            
            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[120px] sm:w-[130px] bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-10" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-[#16202A] border-white/10">
                <SelectItem value="all" className="text-[#F8FAFC]">All Status</SelectItem>
                <SelectItem value="available" className="text-[#F8FAFC]">Available</SelectItem>
                <SelectItem value="sold" className="text-[#F8FAFC]">Sold</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Featured Filter */}
            <Select value={featuredFilter} onValueChange={(v) => { setFeaturedFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[120px] sm:w-[130px] bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-10 hidden sm:flex" data-testid="featured-filter">
                <SelectValue placeholder="Featured" />
              </SelectTrigger>
              <SelectContent className="bg-[#16202A] border-white/10">
                <SelectItem value="all" className="text-[#F8FAFC]">All</SelectItem>
                <SelectItem value="featured" className="text-[#F8FAFC]">Featured</SelectItem>
                <SelectItem value="not_featured" className="text-[#F8FAFC]">Not Featured</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Finance Filter - hidden on mobile */}
            <Select value={financeFilter} onValueChange={(v) => { setFinanceFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[120px] sm:w-[130px] bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-10 hidden md:flex" data-testid="finance-filter">
                <SelectValue placeholder="Finance" />
              </SelectTrigger>
              <SelectContent className="bg-[#16202A] border-white/10">
                <SelectItem value="all" className="text-[#F8FAFC]">All</SelectItem>
                <SelectItem value="enabled" className="text-[#F8FAFC]">Finance On</SelectItem>
                <SelectItem value="disabled" className="text-[#F8FAFC]">Finance Off</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Show Archived Toggle */}
            <button
              type="button"
              onClick={() => { setShowArchived(!showArchived); setPage(1); }}
              className={`flex items-center gap-2 px-3 h-10 text-xs font-sans uppercase tracking-wider border ${
                showArchived 
                  ? 'bg-orange-500/20 border-orange-500/30 text-orange-400' 
                  : 'bg-transparent border-white/10 text-[#94A3B8] hover:text-white'
              }`}
              data-testid="show-archived-toggle"
            >
              {showArchived ? <Eye size={14} /> : <EyeOff size={14} />}
              <span className="hidden sm:inline">Archived</span>
            </button>
            
            <Button type="submit" className="h-10 px-4 bg-[#48CCCD] text-[#020405]" data-testid="filter-btn">
              <Filter size={14} className="mr-2" />
              <span className="hidden sm:inline">Filter</span>
            </Button>
          </form>
        </div>
      </section>

      {/* Cars Table */}
      <section className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={32} className="text-[#48CCCD] animate-spin" />
            </div>
          ) : cars.length === 0 ? (
            <div className="text-center py-12">
              <Car size={48} className="text-[#64748B] mx-auto mb-4" />
              <p className="text-[#94A3B8] font-sans">No cars found</p>
              <Button
                onClick={() => navigate('/dashboard/cars/new')}
                className="mt-4 bg-[#48CCCD] text-[#020405]"
              >
                <Plus size={16} className="mr-2" />
                Add Your First Car
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <table className="w-full min-w-[800px]" data-testid="cars-table">
                <thead>
                  <tr className="border-b border-white/20">
                    <th className="py-3 px-2 text-left text-[10px] uppercase tracking-wider text-[#94A3B8] font-sans">Image</th>
                    <th className="py-3 px-2 text-left text-[10px] uppercase tracking-wider text-[#94A3B8] font-sans">Vehicle</th>
                    <th className="py-3 px-2 text-left text-[10px] uppercase tracking-wider text-[#94A3B8] font-sans">Price</th>
                    <th className="py-3 px-2 text-left text-[10px] uppercase tracking-wider text-[#94A3B8] font-sans">Status</th>
                    <th className="py-3 px-2 text-center text-[10px] uppercase tracking-wider text-[#94A3B8] font-sans">★</th>
                    <th className="py-3 px-2 text-center text-[10px] uppercase tracking-wider text-[#94A3B8] font-sans">$</th>
                    <th className="py-3 px-2 text-left text-[10px] uppercase tracking-wider text-[#94A3B8] font-sans">Updated</th>
                    <th className="py-3 px-2 text-left text-[10px] uppercase tracking-wider text-[#94A3B8] font-sans">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cars.map(car => (
                    <CarRow
                      key={car.id}
                      car={car}
                      onToggleFeatured={handleToggleFeatured}
                      onToggleFinance={handleToggleFinance}
                      onMarkSold={handleMarkSoldClick}
                      onMarkAvailable={handleMarkAvailableClick}
                      onArchive={handleArchive}
                      onUnarchive={handleUnarchive}
                      onEdit={handleEdit}
                      isUpdating={updatingCars.has(car.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Pagination */}
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-center gap-4 mt-6">
              <Button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                variant="outline"
                className="bg-transparent border-white/20 text-[#94A3B8]"
              >
                Previous
              </Button>
              <span className="text-[#94A3B8] font-sans text-sm">
                Page {page} of {Math.ceil(total / PAGE_SIZE)}
              </span>
              <Button
                onClick={() => setPage(p => p + 1)}
                disabled={page * PAGE_SIZE >= total}
                variant="outline"
                className="bg-transparent border-white/20 text-[#94A3B8]"
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default DashboardCarsPage;
